/* eslint-disable @typescript-eslint/no-unused-vars */
import { Accordion } from '@descomplica/foundation-ui-components';
import { observer, useViewModel } from '@descomplica/microfrontend-react';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { MaterialIcon, UploadIcon } from '@/presentation/components/icon/custom-icon';
import IconComponent from '@/presentation/components/icon/icon';
import { EssayStore } from '@/presentation/stores/essay.store';

import Loading from '../../components/loading';
import ConfirmDialog from '../essay/components/confirm-dialog';
import EssayMediaModal from '../essay/components/modal/EssayMediaModal';
import EssayTypeModal from '../essay/components/modal/EssayTypeModal';
import SendEssayMediaAgainModal from '../essay/components/modal/SendEssayMediaAgainModal';
import {
  Breadcrumb,
  ButtonText,
  ChangeThemeButton,
  Container,
  DetailsButton,
  EssayInstructions,
  EssayInstructionsContainer,
  EssayTitle,
  EssayTitleContainer,
  HighlightEssayButtonWrapper,
  InputContainer,
  InputInfoContainer,
  LineCountSpan,
  SendImageButton,
  SidebarContainer,
  SideTextWrapper,
  StyledAccordionItem,
  ThemeInfo,
  ThemeInfoContainer,
  ThemeTag,
  ThemeTitleContainer,
  Title,
  TitleContainer,
  WriteEssayContainer,
} from './styles';
import { WriteEssayViewModel } from './WriteEssay.vm';

const MIN_LINES = 7;
const MAX_LINES = 30;

export const WriteEssay: React.FC = () => {
  const vm = useViewModel(WriteEssayViewModel);
  const essayModel = useViewModel(EssayStore);
  const navigate = useNavigate();
  const { guid } = useParams();

  const openWrite = () => {
    navigate('/essays/topics');
  };

  const openEssay = () => {
    navigate('/essays/all');
  };

  useEffect(() => {
    const controller = new AbortController();
    const fetchEssayData = async (guid) => await vm.getEssay(guid);

    if (guid) {
      fetchEssayData(guid);
    }

    return () => controller.abort();
  }, [guid, vm]);

  useEffect(() => {
    vm.initTranscribedEssay();
  }, [essayModel.transcribedEssay, vm]);

  useEffect(() => {
    if (vm.hasError) {
      navigate('/library');
    }

    if (!vm.isLoading && !vm.currentEssayTheme && !guid) {
      navigate('/essays');
    }
  }, [vm.hasError, vm.currentEssayTheme, navigate, guid, vm.isLoading]);

  if (vm.isLoading) {
    return <Loading />;
  }

  const hasMotivationalTexts =
    vm.currentEssayTheme?.motivationalTexts && vm.currentEssayTheme?.motivationalTexts.length > 0;

  const notEnoughLines = vm.lineCount < MIN_LINES;
  const tooMuchLines = vm.lineCount > MAX_LINES;

  const disabledSendEssayToCorrectionButton = !notEnoughLines && !tooMuchLines;

  return (
    <Container>
      <TitleContainer>
        <Breadcrumb onClick={() => openEssay()}>Redação / Minhas redações</Breadcrumb>
        <Title>Sua redação</Title>
      </TitleContainer>
      <WriteEssayContainer>
        <SidebarContainer>
          <ThemeInfoContainer>
            <ThemeInfo>Sobre o tema</ThemeInfo>
            <ChangeThemeButton onClick={() => navigate('/essays')}>Trocar tema</ChangeThemeButton>
          </ThemeInfoContainer>

          <EssayInstructionsContainer>
            <ThemeTitleContainer>
              <ThemeTag>{`Encceja`}</ThemeTag>
              <ThemeInfo>{vm.currentEssayTheme?.name}</ThemeInfo>
            </ThemeTitleContainer>
            <Accordion>
              <StyledAccordionItem>
                <Accordion.Title>
                  <EssayInstructions>Instruções da redação</EssayInstructions>
                </Accordion.Title>
                <Accordion.Content>
                  <SideTextWrapper>
                    <span>
                      {`Faça um rascunho da sua redação antes de passar para o espaço apropriado para
                        envio. A redação que apresentar cópia dos textos da proposta terá o número de
                        linhas copiadas desconsiderado para efeito de correção. Receberá nota zero, em
                        qualquer das situações expressas a seguir, a redação que: Tiver até 7 (sete)
                        linhas escritas, sendo considerada "texto insuficiente". Fugir ao tema ou que
                        não atender ao tipo dissetrtativo-argumentativo. Apresentar parte do texto
                        deliberadamente desconectada do tema proposto.`}
                    </span>
                  </SideTextWrapper>
                </Accordion.Content>
              </StyledAccordionItem>
              <StyledAccordionItem>
                <Accordion.Title>
                  <EssayInstructions>Textos motivadores</EssayInstructions>
                </Accordion.Title>
                <Accordion.Content>
                  <SideTextWrapper>
                    {hasMotivationalTexts &&
                      vm.currentEssayTheme?.motivationalTexts.map((text, index) => {
                        return (
                          <div key={index}>
                            <span>{text.content}</span>
                            {text?.images.length > 0 &&
                              text?.images.map((image, i) => {
                                return <img key={i} src={image.url} alt={image.description} />;
                              })}
                          </div>
                        );
                      })}
                  </SideTextWrapper>
                </Accordion.Content>
              </StyledAccordionItem>
              <StyledAccordionItem>
                <Accordion.Title>
                  <EssayInstructions>Proposta de Redação</EssayInstructions>
                </Accordion.Title>
                <Accordion.Content>
                  <SideTextWrapper>
                    <span>
                      {`A partir da leitura dos textos motivadores e com base nos conhecimentos construídos ao longo de sua formação, redija texto dissertativo-argumentativo em modalidade escrita formal da língua portuguesa sobre o tema ${vm.currentEssayTheme} apresentando proposta de intervenção que respeite os direitos humanos. Selecione, organize e relacione, de forma coerente e coesa, argumentos e fatos para defesa de seu ponto de vista.`}
                    </span>
                  </SideTextWrapper>
                </Accordion.Content>
              </StyledAccordionItem>
            </Accordion>
          </EssayInstructionsContainer>
        </SidebarContainer>

        <InputContainer>
          <EssayTitleContainer>
            <EssayTitle>Sua redação</EssayTitle>
            <SendImageButton onClick={() => vm.setIsSendEssayMediaAgainModalOpen(true)}>
              <UploadIcon />
              <ButtonText>Reenviar imagem</ButtonText>
            </SendImageButton>
          </EssayTitleContainer>
          <input
            onChange={(e) => {
              vm.setEssayTitle(e.target.value);
            }}
            placeholder="Titulo da redação"
            value={vm.essayTitle}
          />
          <textarea
            name=""
            id=""
            cols={30}
            rows={15}
            placeholder="Digite aqui a sua redação"
            onInput={(essay) => {
              vm.setEssayInput(essay.currentTarget.value);
            }}
            value={vm.essayInput}
          />
          <InputInfoContainer>
            <LineCountSpan error={notEnoughLines || tooMuchLines}>
              {vm.lineCount} linhas{' '}
              {(notEnoughLines && `(mínimo para envio: 7 linhas)`) ||
                (tooMuchLines && `(limite máximo: 30 linhas)`)}{' '}
            </LineCountSpan>
            <span> {(vm.essayInput && vm.essayLength) || 0} caracteres digitados</span>
          </InputInfoContainer>

          <HighlightEssayButtonWrapper>
            <DetailsButton
              disabled={vm.lineCount == 0}
              type="button"
              onClick={() => vm.sentDraftEssay()}
            >
              <MaterialIcon />
              <span>Salvar rascunho</span>
            </DetailsButton>
            <DetailsButton
              disabled={!disabledSendEssayToCorrectionButton}
              type="button"
              onClick={() => vm.sentEssay()}
            >
              <IconComponent name="whiteArrow" />
              <span>Enviar para correção</span>
            </DetailsButton>
          </HighlightEssayButtonWrapper>
        </InputContainer>
      </WriteEssayContainer>
      <ConfirmDialog
        isOpen={vm.essaySent || vm.draftSent}
        title={vm.essaySent ? 'Redação enviada 🎉' : 'Rascunho salvo'}
        description={
          vm.essaySent
            ? 'Sua correção vai aparecer na página das suas redações em breve. Enquanto isso, que tal começar uma nova redação?'
            : 'Continue sua redação a qualquer momento acessando “Minhas Redações”.'
        }
        confirmButtonLabel="Começar nova redação"
        onConfirm={() => openWrite()}
        cancelButtonLabel="Ir para minhas redações"
        onCancel={() => openEssay()}
      />

      <ConfirmDialog isOpen={vm.isSending} isSendingConfirmation />
      <SendEssayMediaAgainModal
        onClose={() => vm.setIsSendEssayMediaAgainModalOpen(false)}
        isOpen={vm.isSendEssayMediaAgainModalOpen}
      />
      <EssayTypeModal
        onClose={() => vm.setIsEssayTypeModalOpen(false)}
        isOpen={vm.isEssayTypeModalOpen}
      />
      <EssayMediaModal
        onClose={() => {
          vm.setIsEssayMediaModalOpen(false);
        }}
        isOpen={vm.isEssayMediaModalOpen}
      />
    </Container>
  );
};

export default observer(WriteEssay);
